import { mapActions } from "vuex";
import { listsMixin } from "@/mixins/listsMixin";
import _ from "lodash";
import { v4 as uuidv4 } from 'uuid';

export const insidePlanMixin = {
  mixins: [ listsMixin ],

  data() {
    return {
      isLoading: false,
      allConnections: null,
      initialConnections: null,
      fibersPortsCache: {
        fiber: {},
        port: {}
      },
      fiberColours: null,
      fiberTypeOptions: null,
      cassetteMaterialsOptions: null,
      addTubeFiberForm: {
        isOpen: false,
        connectionSide: null,
        connectionId: null,
      },
      isRackMenuOpen: false,
      isDrawerMenuOpen: false,
      trayConnectionMenu: {
        isOpen: false,
        fiberId: null,
        side: null,
      },
      menuHorizontalPostion: null,
      menuVerticalPosition: null,
      tubeFiberDialog: {
        isOpen: false,
        typeToDelete: null,
      },
      cassetteForm: {
        isOpen: false,
      },
      action: null,
      objectToEdit: null,
      deleteCassetteDialog: {
        isOpen: false,
        cassetteUuid: null,
        cassetteLabel: null,
      },
      editTubeFiberForm: {
        isOpen: false,
      },
    }
  },

  mounted() {
    this.getInsidePlanLists();
  },

  computed: {
    changedConnections() {
      return _.differenceWith(this.allConnections, this.initialConnections, _.isEqual);
    },

    connectionsWereChanged() {
      return this.changedConnections.length > 0;
    },

    isClosureForm() {
      return this.objectEndpoint && this.objectEndpoint === 'closures';
    }
  },

  methods: {
    ...mapActions([
      "editFibers",
      "deleteCassette",
      "fetchStockMaterialsList",
    ]),

    createConnectionsArray(connections) {
      let finalConnections = [];
      connections.forEach(connection => {
        if (connection.origin.type !== 'port' && connection.destination.type !== 'port') {
          let tray;
          let originObject = connection.origin.type ? this.fibersPortsCache[connection.origin.type][connection.origin.id] : null;
          let destinationObject = connection.destination.type ? this.fibersPortsCache[connection.destination.type][connection.destination.id] : null;
          const origin = originObject ? this.getOriginObject(originObject, connection.origin.type, connection.origin.parent.type) : originObject;
          const destination = destinationObject ? this.getDestinationObject(destinationObject, connection.destination.type, connection.destination.parent.type) : destinationObject;
          if (connection.hasOwnProperty('tray')) {
            tray = {
              trayMaterial: connection.tray.material_label ? connection.tray.material_label : null,
              trayLabel: connection.tray.label ? connection.tray.label : null,
              trayFace: connection.tray.face ? connection.tray.face : null,
              trayNumber: connection.tray.id ? connection.tray.id : null,
              trayIndex: connection.tray.index ? connection.tray.index : null,
              trayParentDrawerId: connection.tray.drawer_id ? connection.tray.drawer_id : null,
            };
          } else {
            tray = {
              trayMaterial: null,
              trayLabel: null,
              trayFace: null,
              trayNumber: null,
              trayIndex: null,
              trayParentDrawerId: null,
            };
          }
          const finalObj = { ...tray, ...origin, ...destination, positionInCassette: connection.position ? connection.position.index : this.highestConnectionPosition(finalConnections), positionId: connection.position ? connection.position.id : null, mode: connection.mode, connectionId: uuidv4() };
          finalConnections.push(finalObj);
        } else {
          finalConnections.push(connection);
        }
      });
      this.allConnections = [...finalConnections];
      this.initialConnections = _.cloneDeep(finalConnections);
    },

    highestConnectionPosition(connections) {
      let highestPosition = 0;
      connections.forEach(connection => {
        highestPosition = connection.positionInCassette > highestPosition ? connection.positionInCassette : highestPosition
      });
      return highestPosition+1;
    },

    getOriginObject(object, type, parentType) {
      let obj;
      if (type === "fiber") {
        obj = {
          originFiberID: object.fiber.fb_id,
          originConnectionType: type,
          originMaterial: object.cable.cb_material_id,
          originLabel: object.fiber.fb_label ? object.fiber.fb_label : `CB:${object.fiber.fb_cable_id}`,
          originTubeColor: this.getTubeColor(object.fiber.fb_nr_tube),
          originTubeId: object.fiber.fb_nr_tube,
          originFiberColor: this.getFiberColor(object.fiber.fb_color),
          originIdentificators: `T${object.fiber.fb_nr_tube}/F${object.fiber.fb_nr_in_tube}`,
          originParentType: parentType.charAt(0).toUpperCase() + parentType.slice(1),
          originFiberLogicalType: object.fiber.fb_logical_type,
          originFiberComment: object.fiber.fb_comment,
        }
      }
      return obj;
    },

    getDestinationObject(object, type, parentType) {
      let obj;
      if (type === "fiber") {
        obj = {
          destinationFiberID: object.fiber.fb_id,
          destinationConnectionType: type,
          destinationMaterial: object.cable.cb_material_id,
          destinationLabel: object.fiber.fb_label ? object.fiber.fb_label : `CB:${object.fiber.fb_cable_id}`,
          destinationTubeColor: this.getTubeColor(object.fiber.fb_nr_tube),
          destinationTubeId: object.fiber.fb_nr_tube,
          destinationFiberColor: this.getFiberColor(object.fiber.fb_color),
          destinationIdentificators: `T${object.fiber.fb_nr_tube}/F${object.fiber.fb_nr_in_tube}`,
          destinationParentType: parentType.charAt(0).toUpperCase() + parentType.slice(1),
          destinationFiberLogicalType: object.fiber.fb_logical_type,
          destinationFiberComment: object.fiber.fb_comment,
        }
      }
      return obj;
    },

    getFiberColor(colorId){
      let finalColor;
      this.fiberColours.forEach(color => {
        if(color.code === String(colorId)){
          finalColor = color.libelle;
        }
      })
      return finalColor;
    },

    getTubeColor(nr) {
      let colorNr = nr % 12;
      if (colorNr === 0) {
        colorNr = 12;
      }
      switch (colorNr) {
        case 1:
          return "#0070C0";
        case 2:
          return "#FD8408";
        case 3:
          return "#00B050";
        case 4:
          return "#974706";
        case 5:
          return "#A6A6A6";
        case 6:
          return "#FFFFFF";
        case 7:
          return "#FF0000";
        case 8:
          return "#000000";
        case 9:
          return "#FFFF00";
        case 10:
          return "#CE1C93";
        case 11:
          return "#FBA5A3";
        case 12:
          return "#7AD3F2";
      }
    },

    async getInsidePlanLists(){
      try {
        this.fiberColours = await this.getList("list_fo_color", [], true);
        this.fiberTypeOptions = await this.getList("list_fo_logical_type", [], true);
        this.cassetteMaterialsOptions = await this.getMaterialOptionsByFamilyID("ct");
      } catch (error) {
        console.error(error);
      } finally {
      }
    },

    async getMaterialOptionsByFamilyID(materialFamilyId){
      const options = [];
      const materials = await this.fetchStockMaterialsList(
        {
          material_family_id: materialFamilyId,
          append: "family_specific_fields"
        }
      );
      if (materialFamilyId !== "ct") {
        materials.forEach(material =>{
          options.push(
            {
              id: material.sm_id,
              label: material.sm_short_description ? material.sm_short_description : material.sm_description
            }
          )
        });
        return options;
      }
      return materials;
    },


    /**
     * Opens the form to add the tube or fiber
     *
     * @param {String} connectionSide the side of the selected fiber
     * @param {Object} cassette an object with the data of the selected cassette
     * @param {Number} leftoverCapacity the number of leftover spaces in the cassette
     * @param {String} connectionId the string of the selected fiber
     */
    openAddTubeFiberForm(connectionSide, cassette, leftoverCapacity, cassetteCapacity, positionInCassette, connectionId) {
      this.addTubeFiberForm.isOpen = true;
      this.addTubeFiberForm.connectionId = connectionId;
      this.addTubeFiberForm.cassette = cassette;
      this.addTubeFiberForm.connectionSide = connectionSide;
      this.addTubeFiberForm.leftoverCapacity = leftoverCapacity;
      this.addTubeFiberForm.cassetteCapacity = cassetteCapacity;
      this.addTubeFiberForm.positionInCassette = positionInCassette;
    },

    /**
     * Closes the add tube/fiber form
     */
    closeAddTubeFiberForm() {
      this.addTubeFiberForm.isOpen = false;
      this.addTubeFiberForm.connectionId = null;
      this.addTubeFiberForm.cassette = null;
      this.addTubeFiberForm.connectionSide = null;
      this.addTubeFiberForm.leftoverCapacity = null;
      this.addTubeFiberForm.cassetteCapacity = null;
      this.addTubeFiberForm.positionInCassette = null;
    },

    /**
     * Indicates the fibers available to be added to connections
     *
     * @returns {Array} the available fibers
     */
    availableFibers(cablesIn, cablesOut) {
      let usedFibers = [];
      if (this.allConnections) {
        this.allConnections.forEach(connection => {
          // For the fiber-fiber connections
          usedFibers = _.union(usedFibers, [connection.originFiberID, connection.destinationFiberID])

          // In case there are fiber-port connections
          if (connection.origin && connection.origin.type === 'fiber') {
            usedFibers = _.union(usedFibers, [connection.origin.id])
          }

          // In case there are port-fiber connections
          if (connection.destination && connection.destination.type === 'fiber') {
            usedFibers = _.union(usedFibers, [connection.destination.id])
          }
        });

        let allFibers = Object.entries(this.fibersPortsCache.fiber);
        let availableFibers = allFibers.filter(fiber => !usedFibers.includes(parseInt(fiber[0])));

        if (this.addTubeFiberForm.connectionSide === 'origin') {
          let cableInIds = cablesIn.map(cable => cable.cb_id);
          return availableFibers.filter(fiber => cableInIds.includes(fiber[1].cable.cb_id));
        } else if (this.addTubeFiberForm.connectionSide === 'destination') {
          let cableOutIds = cablesOut.map(cable => cable.cb_id);
          return availableFibers.filter(fiber => cableOutIds.includes(fiber[1].cable.cb_id));
        } else {
          return availableFibers;
        }
      } else {
        return usedFibers;
      }
    },

    /**
     * Opens the tray connection menu
     *
     * @param {Number} horizontal the horizontal coordinates of the click
     * @param {Number} vertical the vertical coordinates of the click
     * @param {String} fiberId the id of the clicked fiber
     * @param {String} side the side of the clicked fiber
     */
    openTrayConnectionMenu(horizontal, vertical, fiberId, side) {
      if(this.isRackMenuOpen){
        this.isRackMenuOpen = !this.isRackMenuOpen;
      }
      if(this.isDrawerMenuOpen){
        this.isDrawerMenuOpen = !this.isDrawerMenuOpen;
      }
      if(!this.trayConnectionMenu.isOpen){
        this.trayConnectionMenu.isOpen = !this.trayConnectionMenu.isOpen;
      }
      if (window.innerHeight - vertical - 24.11 < 164) {
        vertical -= 164 - (window.innerHeight - vertical - 24.11)
      }
      if (window.innerWidth - horizontal < 136.79) {
        horizontal -= 139 - (window.innerWidth - horizontal)
      }

      // if the menu is opened in the closure form its parent component is the object form and we need to subtract the distance to the top left of the screen
      // else, if the menu is opened in the Inside Plan tab we need to remove 35 pixels from the top and left because of the sidebar and the navbar
      if (this.isClosureForm) {
        let element = document.getElementById('object-form');
        let elementBouding = element.getBoundingClientRect();
        vertical -= elementBouding.top;
        horizontal -= elementBouding.left;
      } else {
        vertical -= 35;
        horizontal -= 35;
      }

      this.menuHorizontalPostion = horizontal;
      this.menuVerticalPosition = vertical;
      this.trayConnectionMenu.fiberId = fiberId;
      this.trayConnectionMenu.side = side;
    },

    openCassetteForm(cassetteData) {
      if(this.isDrawerMenuOpen){
        this.isDrawerMenuOpen = !this.isDrawerMenuOpen;
      }
      if(this.trayConnectionMenu.isOpen){
        this.trayConnectionMenu.isOpen = !this.trayConnectionMenu.isOpen;
      }
      if(this.isRackMenuOpen){
        this.isRackMenuOpen = !this.isRackMenuOpen;
      }
      if (!this.cassetteForm.isOpen) {
        this.cassetteForm.isOpen = !this.cassetteForm.isOpen;
      }
      this.action = !!cassetteData ? "edit" : "create";
      this.objectToEdit = {...cassetteData}
    },

    closeCassetteForm() {
      this.cassetteForm.isOpen = false;
      this.objectToEdit = null;
      this.action = null;
    },

    openDeleteCassetteDialog(cassetteData) {
      this.closeInsidePlanMenu();
      this.deleteCassetteDialog.isOpen = true;
      this.deleteCassetteDialog.cassetteUuid = cassetteData.uuid;
      this.deleteCassetteDialog.cassetteLabel = cassetteData.label;
    },

    closeDeleteCassetteDialog() {
      this.deleteCassetteDialog.isOpen = false;
      this.deleteCassetteDialog.cassetteUuid = null;
      this.deleteCassetteDialog.cassetteLabel = null;
    },

    closeInsidePlanMenu(){
      if(this.isDrawerMenuOpen){
        this.isDrawerMenuOpen = !this.isDrawerMenuOpen;
      }
      if(this.isRackMenuOpen){
        this.isRackMenuOpen = !this.isRackMenuOpen;
      }
      if(this.trayConnectionMenu.isOpen){
        this.trayConnectionMenu.isOpen = !this.trayConnectionMenu.isOpen;
      }
      this.objectToEdit = null;
      // //set all active racks to false
      // this.formattedRacks.forEach(rack =>{
      //   if(rack.active){
      //     rack.active = !rack.active;
      //   }
      // })
    },

    /**
     * Closes the edit tube/fiber form
     */
    closeEditTubeFiberForm() {
      this.editTubeFiberForm.fiberData = null;
      this.editTubeFiberForm.isOpen = false;
      this.editTubeFiberForm.objectTypeToEdit = null;
    },

    /**
     * Opens the edit tube/fiber form
     */
    openEditTubeFiberForm(objectTypeToEdit) {
      if (objectTypeToEdit === "fiber") {
        let fiberData = this.fibersPortsCache.fiber[this.trayConnectionMenu.fiberId].fiber
        this.editTubeFiberForm.fiberData = {
          comment: fiberData.fb_comment,
          logical_type: fiberData.fb_logical_type,
        }
      } else if (objectTypeToEdit === "tube") {
        this.editTubeFiberForm.fiberData = {
          comment: null,
          logical_type: this.tubeFibersLogicalType(),
        }
      }
      this.editTubeFiberForm.isOpen = true;
      this.editTubeFiberForm.objectTypeToEdit = objectTypeToEdit;
      this.closeInsidePlanMenu();
    },

    tubeFibersLogicalType() {
      let selectedFiberSide = this.trayConnectionMenu.side;
      let selectedFiberId = this.trayConnectionMenu.fiberId;
      let selectedFiberCableId = this.fibersPortsCache.fiber[selectedFiberId].fiber.fb_cable_id;
      let selectedFiberTubeNr = this.fibersPortsCache.fiber[selectedFiberId].fiber.fb_nr_tube;
      let tubeConnections = this.allConnections.filter(connection =>
          connection[selectedFiberSide+"Label"] &&
          connection[selectedFiberSide+"Label"].includes(selectedFiberCableId) &&
          connection[selectedFiberSide+"TubeId"] === selectedFiberTubeNr
      );
      let connectionLogicalType = tubeConnections[0][selectedFiberSide+'FiberLogicalType'];
      let tubeConnectionsHaveSameLogicalType = false;

      for (let i = 0; i < tubeConnections.length; i++) {
        const connection = tubeConnections[i];
        tubeConnectionsHaveSameLogicalType = connection[selectedFiberSide+'FiberLogicalType'] === connectionLogicalType
        if (!tubeConnectionsHaveSameLogicalType) break;
      }

      return tubeConnectionsHaveSameLogicalType ? connectionLogicalType : null;
    },

    /**
     * Opens the delete tube/fiber dialog
     *
     * @param {String} type the type of the object to delete, either fiber or tube
     */
    openDeleteTubeFiberDialog(type) {
      this.tubeFiberDialog.typeToDelete = type;
      this.tubeFiberDialog.isOpen = true;
      this.closeInsidePlanMenu();
    },

    /**
     * Closes the delete tube/fiber dialog
     */
    closeTubeFiberDialog() {
      this.tubeFiberDialog.typeToDelete = null;
      this.tubeFiberDialog.isOpen = false;
    },

    async editTubeFiber(objectData) {
      let fibersToEdit = [];
      let dataToSend = [];
      let selectedFiberId = this.trayConnectionMenu.fiberId;
      let selectedFiberSide = this.trayConnectionMenu.side;
      try {
        this.isLoading = true;
        if (this.editTubeFiberForm.objectTypeToEdit === "fiber") {
          fibersToEdit.push(selectedFiberId);
        } else if (this.editTubeFiberForm.objectTypeToEdit === "tube") {
          this.allConnections.forEach(connection => {
            if (
              connection[selectedFiberSide+"Label"] &&
              connection[selectedFiberSide+"Label"].includes(this.fibersPortsCache.fiber[selectedFiberId].fiber.fb_cable_id) &&
              connection[selectedFiberSide+"TubeId"] === this.fibersPortsCache.fiber[selectedFiberId].fiber.fb_nr_tube
            ) {
              fibersToEdit.push(connection[selectedFiberSide+'FiberID']);
            }
          });
        }

        fibersToEdit.forEach(fiberId => {
          let fiberToUpdateConnectionIndex = this.allConnections.findIndex(connection => connection.originFiberID === fiberId || connection.destinationFiberID === fiberId);
          this.allConnections[fiberToUpdateConnectionIndex][selectedFiberSide+'FiberComment'] = objectData.comment;
          this.allConnections[fiberToUpdateConnectionIndex][selectedFiberSide+'FiberLogicalType'] = objectData.logical_type;
          this.fibersPortsCache.fiber[selectedFiberId].fiber.fb_comment = objectData.comment;
          this.fibersPortsCache.fiber[selectedFiberId].fiber.fb_logical_type = objectData.logical_type;
          dataToSend.push({...objectData, uuid: this.fibersPortsCache.fiber[fiberId].fiber.fb_uuid });
        });

        await this.editFibers(dataToSend);
        this.updateSplicingConnections(this.allConnections);
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
        this.closeEditTubeFiberForm();
      }
    },

    /**
     * Deletes the tube/fiber
     */
    deleteTubeFiber() {
      let connections = [...this.allConnections];

      for (let i = 0; i < connections.length; i++) {
        let connection = connections[i];
        if (this.connectionSideCanBeCleared(connection, this.trayConnectionMenu.side)) {
          Object.keys(connection).forEach(k => { if (k.includes(this.trayConnectionMenu.side)) { connection[k] = null }})
        }
      }

      this.allConnections = [...connections];
      this.updateSplicingConnections(connections)
      this.closeTubeFiberDialog();
    },

    /**
     * Checks if the connection side can be cleared
     *
     * @param {Object} connection the connection to be checked
     * @param {String} connectionSideToBeCleared the connection side to be checked
     *
     * @returns {Boolean}
     */
    connectionSideCanBeCleared(connection, connectionSideToBeCleared) {
      return this.tubeFiberDialog.typeToDelete === "fiber"
        ? connection[connectionSideToBeCleared+"FiberID"] === this.trayConnectionMenu.fiberId
        : connection[connectionSideToBeCleared+"Label"] &&
          connection[connectionSideToBeCleared+"Label"].includes(this.fibersPortsCache.fiber[this.trayConnectionMenu.fiberId].fiber.fb_cable_id) &&
          connection[connectionSideToBeCleared+"TubeId"] === this.fibersPortsCache.fiber[this.trayConnectionMenu.fiberId].fiber.fb_nr_tube
    },

    async confirmDeleteCassette() {
      try {
        let cassetteUUID = this.deleteCassetteDialog.cassetteUuid;
        this.closeDeleteCassetteDialog();
        this.isLoading = true;
        await this.deleteCassette(cassetteUUID);
        if (this.isClosureForm) {
          this.getClosureCassettes();
          this.getAllConnections();
        }
      } catch (error) {
        console.error(error);
        this.$toasted.global.toast_error(error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Adds the tube/fiber to the connections
     *
     * @param {String} objectId the id of the object to be added
     * @param {String} objectType the type of the object to be added, either fiber or tube
     * @param {String} cableId the id of the selected cable
     */
    addTubeFiber(objectId, objectType, cableId) {
      let connections = [...this.allConnections];
      let connectionSide = this.addTubeFiberForm.connectionSide;
      let connectionIndex = connections.findIndex(connection => connection.connectionId === this.addTubeFiberForm.connectionId);
      try {
        if (objectType === "fiber") {
          let object = this.generateConnectionFiberObject(objectId, this.fibersPortsCache.fiber[objectId], connectionSide);
          if (this.addTubeFiberForm.connectionSide && connectionIndex > -1) {
            connections[connectionIndex] = {
              ...connections[connectionIndex],
              mode: "SPLICE",
              ...object
            };
          } else {
            connections.push({
              ...this.addTubeFiberForm.cassette,
              ...object,
              mode: "STOCK",
              connectionId: uuidv4(),
              positionInCassette: this.addTubeFiberForm.positionInCassette,
            });
          }
        } else if (objectType === "tube") {
          let availableFibers = [];
          if (this.isClosureForm) {
            availableFibers = this.availableFibers(this.origin.cables, this.destination.cables);
          } else {
            availableFibers = this.availableFibers(this.techLocal.cable_in, this.techLocal.cable_out);
          }
          let tubeFibers = availableFibers.filter(fiber => fiber[1].fiber.fb_nr_tube === objectId && fiber[1].cable.cb_id === cableId)
          if (this.cassetteHasEnoughSpace(tubeFibers.length)) {
            tubeFibers.forEach((fiber, i) => {
              let formattedFiberObject = this.generateConnectionFiberObject(fiber[1].fiber.fb_id, fiber[1], connectionSide);
              let existingConnectionIndex = connections.findIndex(connection =>
                connection.trayNumber === this.addTubeFiberForm.cassette.trayNumber &&
                connection.positionInCassette === this.addTubeFiberForm.positionInCassette+i
              )
              if (existingConnectionIndex > -1) {
                connections[existingConnectionIndex] = {
                  ...connections[existingConnectionIndex],
                  mode: "SPLICE",
                  ...formattedFiberObject
                };
              } else {
                connections.push({
                  ...this.addTubeFiberForm.cassette,
                  ...formattedFiberObject,
                  mode: "STOCK",
                  connectionId: uuidv4(),
                  positionInCassette: this.addTubeFiberForm.positionInCassette+i,
                });
              }
            })
          } else {
            throw new TypeError(this.$i18n.t("CassetteHasNoCapacityForFibers"))
          }
        }
        this.allConnections = [...connections];
        this.updateSplicingConnections(connections)
      } catch (error) {
        console.error(error);
        this.$toasted.global.toast_error(error.message);
      }
      this.closeAddTubeFiberForm();
    },

    /**
     * Generates the object with the information to be added to the connection
     *
     * @param {String} objectId the id of the fiber to be added
     * @param {Object} fiberInfo the info of the fiber to be added
     * @param {String} connectionSide the side of the connection where the fiber is going to be added
     * @returns {Object}
     */
    generateConnectionFiberObject(objectId, fiberInfo, connectionSide) {
      let object = {
        [connectionSide+'FiberID']: objectId,
        [connectionSide+'ConnectionType']: "fiber",
        [connectionSide+'Material']: fiberInfo.cable.cb_material_id,
        [connectionSide+'Label']: fiberInfo.fiber.fb_label ? fiberInfo.fiber.fb_label : `CB:${fiberInfo.fiber.fb_cable_id}`,
        [connectionSide+'TubeColor']: this.getTubeColor(fiberInfo.fiber.fb_nr_tube),
        [connectionSide+'TubeId']: fiberInfo.fiber.fb_nr_tube,
        [connectionSide+'FiberColor']: this.getFiberColor(fiberInfo.fiber.fb_color),
        [connectionSide+'Identificators']: `T${fiberInfo.fiber.fb_nr_tube}/F${fiberInfo.fiber.fb_nr_in_tube}`,
        [connectionSide+'FiberComment']: fiberInfo.fiber.fb_comment,
        [connectionSide+'FiberLogicalType']: fiberInfo.fiber.fb_logical_type,
      }
      return object;
    },

    /**
     * Checks if the cassette has enough leftover space
     *
     * @param {Number} nrFibersToAdd number of fibers to be added
     * @returns {Boolean}
     */
    cassetteHasEnoughSpace(nrFibersToAdd) {
      let connectionSide = this.addTubeFiberForm.connectionSide;
      let positionInCassetteToAdd = this.addTubeFiberForm.positionInCassette
      let cassetteConnections = this.allConnections.filter(connection =>
        connection.trayNumber === this.addTubeFiberForm.cassette.trayNumber &&
        connection.positionInCassette >= positionInCassetteToAdd &&
        connection[connectionSide+'FiberID']
      );
      let numberOfCassetteConnections = cassetteConnections.length;
      let availableConsecutiveConnectionSpots = numberOfCassetteConnections > 0
        ? cassetteConnections[0].positionInCassette - positionInCassetteToAdd
        : this.addTubeFiberForm.cassetteCapacity - this.addTubeFiberForm.positionInCassette + 1;
      return availableConsecutiveConnectionSpots >= nrFibersToAdd;
    },

    /**
     * Formats the connections according to the requests' needs
     */
    formatConnections(connections) {
      let formattedConnections = [];
      connections.forEach((connection) => {
        let formattedConnection;
        // In case the connection is fiber-port or port-fiber
        if (!!connection.origin && !!connection.destination) {
          formattedConnection = connection;
        }
        // In case the connection is fiber-fiber
        else {
          formattedConnection = {
            destination: {
              id: connection.destinationFiberID,
              logical_type: null,
              parent: {
                id: connection.destinationFiberID ? this.fibersPortsCache.fiber[connection.destinationFiberID].cable.cb_id : null,
                type: connection.destinationFiberID ? "cable" : null,
                uuid: connection.destinationFiberID ? this.fibersPortsCache.fiber[connection.destinationFiberID].cable.cb_uuid : null,
              },
              type: connection.destinationConnectionType,
              uuid: connection.destinationFiberID ? this.fibersPortsCache.fiber[connection.destinationFiberID].fiber.fb_uuid : null
            },
            mode: connection.mode,
            origin: {
              id: connection.originFiberID,
              logical_type: null,
              parent: {
                id: connection.originFiberID ? this.fibersPortsCache.fiber[connection.originFiberID].cable.cb_id : null,
                type: connection.originFiberID ? "cable" : null,
                uuid: connection.originFiberID ? this.fibersPortsCache.fiber[connection.originFiberID].cable.cb_uuid : null,
              },
              type: connection.originConnectionType,
              uuid: connection.originFiberID ? this.fibersPortsCache.fiber[connection.originFiberID].fiber.fb_uuid : null
            },
            position: {
              index: connection.positionInCassette,
              id: connection.positionId,
            },
            same_side_connection: false,
            tray: {
              drawer_id: connection.trayParentDrawerId,
              id: connection.trayNumber,
              label: connection.trayLabel,
              index: connection.trayIndex,
              face: connection.trayFace,
              material_label: connection.trayMaterial,
            }
          }
        }
        formattedConnections.push(formattedConnection);
      });
      return formattedConnections;
    },

    updateSplicingConnections(connections) {
      if (this.isClosureForm) {
        this.updateLogicalSplicingConnections(this.formatConnections(connections));
      }
    }
  }
}


// WEBPACK FOOTER //
// ./src/mixins/insidePlanMixin.js